/**
 * Os pedidos de Terraform de uma conversa, achados pelos cartões que ela mostra (spec
 * 2026-11-04-painel-do-devops §3.2): de onde sai o botão "Andamento do runner", a âncora
 * do painel por stage e a mensagem para onde o "Ir ao cartão" rola.
 *
 * Só conta mensagem que DESENHA o cartão (`bot`, ou o alerta que o `cartaoDoAviso`
 * aceita): rolar até uma mensagem sem cartão não leva a decisão nenhuma.
 *
 * O stage de cada pedido sai de `params.stage_chave` da proposta. É confiável: o servidor
 * cria o pedido com `stage_key = stage_resolver.por_chave(params['stage_chave']).chave`
 * (`proposta_do_chat.py`), uma busca exata por chave, e só depois escreve o `pedido_id`
 * nos MESMOS params (`chat_bot.py`, `{**params, 'pedido_id': ...}`) — o cartão de decisão
 * da D-2 é uma cópia deles. Não custa leitura nenhuma. Proposta sem `stage_chave` fica num
 * grupo só dela; o stage de verdade, o painel mostra pela resposta do servidor.
 */
import type { ChatMessage, ChatTab } from '@/api/chat'
import type { ProposedAction } from './components/ActionProposalCard'
import { cartaoDoAviso, pedidoDoCartao } from './cartaoDoAviso'

export interface CartaoDoPedido {
  pedidoId: number
  /** A mensagem MAIS RECENTE com o cartão deste pedido. */
  mensagemId: number
  stage: string | null
  /** A aba onde a mensagem mora (conversa com abas); `undefined` no thread único. */
  aba?: ChatTab
}

export interface AncoraDoStage {
  /** `params.stage_chave`; `null` na proposta que não o trouxe. */
  stage: string | null
  pedidoId: number
}

function propostaDaMensagem(msg: ChatMessage): ProposedAction | null {
  const pa = msg.metadata?.proposed_action
  return pa && typeof pa === 'object' && !Array.isArray(pa) ? (pa as ProposedAction) : null
}

export function cartaoDaMensagem(msg: ChatMessage): { pedidoId: number; stage: string | null } | null {
  const pa = propostaDaMensagem(msg)
  if (!pa || !(msg.kind === 'bot' || cartaoDoAviso(msg.kind, pa))) return null
  const pedidoId = pedidoDoCartao(pa)
  if (pedidoId === null) return null
  const stage = (pa.params as Record<string, unknown> | null | undefined)?.stage_chave
  return { pedidoId, stage: typeof stage === 'string' && stage ? stage : null }
}

/** Um cartão por pedido: o da mensagem mais recente (maior id). */
export function cartoesDosPedidos(
  fontes: { aba?: ChatTab; mensagens: ChatMessage[] }[],
): Map<number, CartaoDoPedido> {
  const porPedido = new Map<number, CartaoDoPedido>()
  for (const { aba, mensagens } of fontes) {
    for (const msg of mensagens) {
      const cartao = cartaoDaMensagem(msg)
      if (!cartao) continue
      const atual = porPedido.get(cartao.pedidoId)
      const maisNovo = !atual || msg.id > atual.mensagemId
      // A cópia da D-2 pode vir sem o stage; outro cartão do pedido o tinha — e esse
      // pode ser lido DEPOIS do mais novo (outra aba, outra ordem): o stage se calcula
      // antes de descartar a mensagem por ser mais velha.
      const stage = maisNovo
        ? cartao.stage ?? atual?.stage ?? null
        : atual.stage ?? cartao.stage
      porPedido.set(cartao.pedidoId, maisNovo
        ? { pedidoId: cartao.pedidoId, mensagemId: msg.id, aba, stage }
        : { ...atual, stage })
    }
  }
  return porPedido
}

/** A âncora de cada stage é o pedido mais recente dele (maior id); o primeiro da
 * lista é o stage do pedido mais recente da conversa. */
export function ancorasPorStage(cartoes: Map<number, CartaoDoPedido>): AncoraDoStage[] {
  const porStage = new Map<string, AncoraDoStage>()
  for (const { pedidoId, stage } of cartoes.values()) {
    const chave = stage ?? `#${pedidoId}`
    const atual = porStage.get(chave)
    if (!atual || pedidoId > atual.pedidoId) porStage.set(chave, { stage, pedidoId })
  }
  return [...porStage.values()].sort((a, b) => b.pedidoId - a.pedidoId)
}
